import * as React from "react";
import Note from "../../../model/note/Note";
import {JobEventsType} from "../../../model/job/event/JobEvent";
import JobAppointmentEvent from "../../../model/job/event/JobAppointmentEvent";
import JobActivityAppointmentItem from "./items/JobActivityAppointmentItem";
import JobAppointmentStatusEvent from "../../../model/job/event/JobAppointmentStatusEvent";
import CommonStreamItem from "../../../hiberUiKit/src/molecules/StreamItem/CommonStreamItem";
import JobActivityNoteItem from "./items/JobActivityNoteItem";
import {LeadEventType} from "../../../model/lead/event/LeadEvent";
import LeadAppointmentEvent from "../../../model/lead/event/LeadAppointmentEvent";
import LeadAppointmentStatusEvent from "../../../model/lead/event/LeadAppointmentStatusEvent";
import LeadActivityAppointmentItem from "../../activityStream/items/LeadActivityAppointmentItem";
import {LeadActivityItemsType} from "../../activityStream/LeadActivityStreamItems";
import ActivityStreamUtils from "../../../utils/ActivityStreamUtils";
import LeadActivityNoteItem from "../../activityStream/items/LeadActivityNoteItem";
import CommonActivityEmailItem from "../../activityStream/items/CommonActivityEmailItem";
import JobEmailSentEvent from "../../../model/job/event/JobEmailSentEvent";
import JobQuoteAcceptedEvent from "../../../model/job/event/JobQuoteAcceptedEvent";
import JobActivityQuoteItemWithAction from "./items/JobActivityQuoteItemWithAction";
import JobStatusEvent from "../../../model/job/event/JobStatusEvent";
import JobActivityStatusChangedWithAction from "./items/JobActivityStatusChangedWithAction";
import {InvoiceActivityItemsType} from "../../accounting/activityStream/InvoiceActivityStream";
import AccountingNote from "../../../model/invoice/note/AccountingNote";
import InvoiceActivityNoteItem from "../../accounting/activityStream/items/InvoiceActivityNoteItem";
import InvoiceUpdatedEvent from "../../../model/invoice/event/InvoiceUpdatedEvent";
import InvoiceSentEvent from "../../../model/invoice/event/InvoiceSentEvent";
import InvoiceActivitySentItemWithAction from "../../accounting/activityStream/items/InvoiceActivitySentItemWithAction";
import Payment from "../../../model/payment/Payment";
import {InvoiceActivityPaymentItem} from "../../accounting/activityStream/items/InvoiceActivityPaymentItem";
import {AccountingCommonActivityUpdatedItem} from "../../accounting/activityStream/items/AccountingCommonActivityUpdatedItem";
import JobFilesAttachedEvent from "../../../model/job/event/JobFilesAttachedEvent";
import JobFilesAttachedItem from "./items/JobFilesAttachedItem";
import JobMarkedAsPaidEvent from "../../../model/job/event/JobMarkedAsPaidEvent";
import JobActivityMarkAsPaidItem from "./items/JobActivityMarkAsPaidItem";

export type JobActivityItemsType = JobEventsType | LeadEventType | Note;

interface Props {
	/**
	 * Job activity items.
	 */
	activityItems: JobActivityItemsType[];
	/**
	 * Lead activity items.
	 */
	leadActivityItems: LeadActivityItemsType[];
	/**
	 * Invoice activity items.
	 */
	invoiceActivityItems: InvoiceActivityItemsType[];
	/**
	 * Note id to set active class and focus on it.
	 */
	activeNoteId: string;
	/**
	 * Edit note click action.
	 * @param note note to edit.
	 */
	onEditNoteClick: (note: Note) => void;
	/**
	 * Edit appointment click action.
	 * @param appointment to edit.
	 */
	onEditAppointmentClick: (appointmentId: string) => void;
}

/**
 * Job activity stream items component.
 */
export default class JobActivityStreamItems extends React.PureComponent<Props, {}> {
	render() {
		const allActivityItems = [].concat(this.props.activityItems)
			.concat(this.props.leadActivityItems).concat(this.props.invoiceActivityItems).sort(ActivityStreamUtils.sort);
		return allActivityItems.map((activity, index) => {
			const key = activity.id || index;
			const isLeadActivity = this.props.leadActivityItems.some(e => e.id === activity.id);
			if (activity instanceof Note) {
				if (isLeadActivity) {
					return (
						<LeadActivityNoteItem key={key}
											  activity={activity}
											  isActive={this.isActive(activity.id)}
											  readOnly={true}
											  differentContext={true}/>
					)
				}
				return (
					<JobActivityNoteItem key={key} activity={activity}
										 isActive={this.isActive(activity.id)}
										 onEditNoteClick={this.props.onEditNoteClick}/>
				)
			}
			if (activity instanceof JobAppointmentEvent || activity instanceof JobAppointmentStatusEvent) {
				return (
					<JobActivityAppointmentItem key={key}
												activity={activity}
												onEditAppointmentClick={this.props.onEditAppointmentClick}/>
				);
			}
			if (activity instanceof LeadAppointmentEvent || activity instanceof LeadAppointmentStatusEvent) {
				return <LeadActivityAppointmentItem key={key}
													activity={activity}
													readOnly={true}/>
			}
			if (activity instanceof JobEmailSentEvent) {
				return <CommonActivityEmailItem key={key} activity={activity}/>
			}
			if (activity instanceof JobQuoteAcceptedEvent) {
				return <JobActivityQuoteItemWithAction key={key} activity={activity}/>
			}
			if (activity instanceof JobStatusEvent) {
				return <JobActivityStatusChangedWithAction key={key} activity={activity}/>
			}
			if (activity instanceof AccountingNote) {
				return <InvoiceActivityNoteItem activity={activity} readOnly={true}/>;
			}
			if (activity instanceof InvoiceUpdatedEvent) {
				return <AccountingCommonActivityUpdatedItem activity={activity}/>;
			}
			if (activity instanceof InvoiceSentEvent) {
				return <InvoiceActivitySentItemWithAction activity={activity}/>;
			}
			if (activity instanceof Payment) {
				return <InvoiceActivityPaymentItem activity={activity}/>;
			}
			else if (activity instanceof JobFilesAttachedEvent) {
				return <JobFilesAttachedItem key={key} activity={activity}/>
			}
			else if (activity instanceof JobMarkedAsPaidEvent) {
				return <JobActivityMarkAsPaidItem key={key} activity={activity}/>
			}
			return <CommonStreamItem key={key} event={activity} readOnly={isLeadActivity}/>;
		})
	}

	/**
	 * Check if note is the active one.
	 */
	isActive(noteId: string): boolean {
		return this.props.activeNoteId === noteId;
	}
}

